// [COMEMORATIVO-COPA] Banner comemorativo da Copa do Mundo.
//
// Recurso 100% isolado. Para inativar: defina ENABLED = false (abaixo) ou
// ativo:false na config do banco (ver README). Para remover de vez: apague a
// pasta `frontend/comemorativo/` e as 2 linhas marcadas com [COMEMORATIVO-COPA]
// em `frontend/siggma/index.js`.
//
// Quem decide o dia de jogo é o servidor: o bootstrap PHP lê a config
// `comemorativo_copa` (tabela config, EMP_SISTEMA) e envia o jogo de hoje em
// Siggma.copaJogoHoje (ou null). Aqui só renderizamos o que vier.
//
// Comportamento (acionado no fim de Siggma.start, portanto só logado):
//  - Sem jogo do Brasil hoje (copaJogoHoje vazio): não faz nada.
//  - Em dia de jogo, no 1o acesso do dia por usuário: grava o marcador diário
//    (cookie cmm-copa-anim=hoje, lido também pelo bootstrap PHP para pular a
//    query getRevenda). Cliente de revenda (Siggma.clienteRevenda) para por aqui.
//    Demais: anima o banner ~6s com o jogo do dia. Respeita reduced-motion.
//  - Demais acessos do dia: nada (o cookie diário barra tudo).
import styles from "./styles";

// Kill-switch: false desativa o recurso sem remover os arquivos.
const ENABLED = true;

const STORAGE_KEY = "cmm-copa-anim";
const STYLE_ID = "cmm-copa-style";
const DURACAO_MS = 6000;
const FADE_MS = 600;

// Posição do banner na tela: "top" (topo) ou "bottom" (rodapé).
const POSICAO = "bottom";

const CONFETE_CORES = ["#009C3B", "#FFDF00", "#002776", "#FFFFFF"];
const CONFETE_QTD = 18;

function hojeISO() {
  const d = new Date();
  const mes = String(d.getMonth() + 1).padStart(2, "0");
  const dia = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mes}-${dia}`;
}

function lerCookie(nome) {
  const alvo = `${nome}=`;
  for (const parte of document.cookie.split(";")) {
    const c = parte.trim();
    if (c.startsWith(alvo)) return c.slice(alvo.length);
  }
  return "";
}

function jaAnimouHoje() {
  return lerCookie(STORAGE_KEY) === hojeISO();
}

function marcarAnimado() {
  // Cookie (não localStorage) para o bootstrap PHP poder ler e pular a query
  // getRevenda enquanto o marcador for de hoje. ~2 dias de vida; o reset diário
  // vem da comparação da data, não da expiração.
  document.cookie =
    `${STORAGE_KEY}=${hojeISO()}; path=/; max-age=172800; SameSite=Lax`;
}

function prefereMenosMovimento() {
  return globalThis.matchMedia?.("(prefers-reduced-motion: reduce)")
    ?.matches === true;
}

function rnd(min, max) {
  return min + Math.random() * (max - min);
}

function montarConfetti() {
  const confetti = document.createElement("div");
  confetti.className = "cmm-copa-confetti";
  for (let i = 0; i < CONFETE_QTD; i++) {
    const peca = document.createElement("i");
    peca.className = "cmm-copa-confete";
    const tam = Math.round(rnd(6, 12));
    peca.style.left = `${Math.round(rnd(2, 98))}%`;
    peca.style.width = `${tam}px`;
    peca.style.height = `${tam}px`;
    peca.style.background = CONFETE_CORES[i % CONFETE_CORES.length];
    peca.style.animationDelay = `${rnd(0, 1.4).toFixed(2)}s`;
    peca.style.animationDuration = `${rnd(1.6, 3).toFixed(2)}s`;
    if (i % 3 === 0) peca.style.borderRadius = "50%";
    confetti.appendChild(peca);
  }
  return confetti;
}

function montarConteudo(jogo) {
  const content = document.createElement("div");
  content.className = "cmm-copa-content";

  const head = document.createElement("div");
  head.className = "cmm-copa-head";

  // Kicker: fase (+ horário, quando houver). Ex.: "Fase de grupos · 19:00".
  const kicker = document.createElement("span");
  kicker.className = "cmm-copa-kicker";
  kicker.textContent = jogo.horario
    ? `${jogo.fase} · ${jogo.horario}`
    : jogo.fase || "Copa do Mundo · 2026";

  // Título: o confronto quando os dois times estão definidos; senão "Vai,
  // Brasil!" (mata-mata sem adversário ainda preenchido na config).
  const title = document.createElement("strong");
  title.className = "cmm-copa-title";
  title.textContent = jogo.mandante && jogo.visitante
    ? `${jogo.mandante} × ${jogo.visitante}`
    : "Vai, Brasil!";

  head.append(kicker, title);

  // Local (opcional).
  if (jogo.local) {
    const local = document.createElement("span");
    local.className = "cmm-copa-local";
    local.textContent = jogo.local;
    head.append(local);
  }

  const flag = document.createElement("span");
  flag.className = "cmm-copa-flag";
  flag.textContent = "🇧🇷";

  content.append(head, flag);
  return content;
}

function animarBanner(jogo) {
  const style = document.createElement("style");
  style.id = STYLE_ID;
  style.textContent = styles;
  document.head.appendChild(style);

  const banner = document.createElement("div");
  banner.className = `cmm-copa-banner cmm-copa--${POSICAO}`;

  const glow = document.createElement("div");
  glow.className = "cmm-copa-glow";

  const rays = document.createElement("div");
  rays.className = "cmm-copa-rays";

  const ball = document.createElement("span");
  ball.className = "cmm-copa-ball";
  ball.textContent = "⚽";

  banner.append(glow, rays, montarConfetti(), ball, montarConteudo(jogo));
  document.body.appendChild(banner);

  globalThis.setTimeout(() => {
    banner.classList.add("cmm-copa-out");
    globalThis.setTimeout(() => {
      banner.remove();
      style.remove();
    }, FADE_MS);
  }, DURACAO_MS);
}

export function initComemorativoCopa() {
  if (!ENABLED) return;
  if (!globalThis.Siggma?.usuario) return;

  // O servidor (bootstrap PHP) decide o dia de jogo lendo a config no banco e
  // envia o jogo de hoje. Sem jogo do Brasil hoje: nada.
  const jogo = globalThis.Siggma?.copaJogoHoje;
  if (!jogo) return;

  // Gate diário (cookie cmm-copa-anim): 1x por dia por usuário. Enquanto o
  // cookie for de hoje, o bootstrap PHP pula a query getRevenda. Marcado para
  // TODOS — inclusive revenda — para não repetir a consulta no resto do dia.
  if (jaAnimouHoje()) return;
  marcarAnimado();

  // Clientes de revenda não recebem o recurso comemorativo (mesma regra da
  // tela 1942/Avisos: cliente cujo vendedor é uma revenda). Flag vinda do
  // bootstrap em modulos/index.phtml.
  if (globalThis.Siggma?.clienteRevenda) return;

  // Acessibilidade: sem animação.
  if (prefereMenosMovimento()) return;

  animarBanner(jogo);
}
